import React, { useState, useEffect, useRef } from "react";
import { MoreVertical, FileText, Plus, Edit, Trash2, Eye, Archive, HelpCircle, Star } from "lucide-react";
import { useContentManagementContext } from "../ContentManagementContext";
import CreateBlogModal from "./CreateBlogModal";
import CreateFAQModal from "./CreateFAQModal";

const ContentTable = ({ activeTab, searchTerm = "" }) => {
  const { 
    blogPosts, 
    blogPostsLoading, 
    blogPagination,
    faqs,
    faqsLoading,
    faqPagination,
    getStatusColor, 
    updateBlogStatus, 
    deleteBlogPost, 
    changeBlogPage,
    updateFAQStatus,
    deleteFAQ,
    changeFaqPage
  } = useContentManagementContext();
  
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showActionsMenu &&
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target)
      ) {
        setShowActionsMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionsMenu]);

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingBlogId(null);
  };

  // Filter data based on search term
  const filterBySearch = (data, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "") {
      return data;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    return data.filter(item => {
      // Search in title
      if (item.title && item.title.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in author
      if (item.author && item.author.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in category
      if (item.category && item.category.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in tags (if tags is an array)
      if (item.tags) {
        const tagsArray = Array.isArray(item.tags) ? item.tags : [item.tags];
        if (tagsArray.some(tag => tag && tag.toLowerCase().includes(searchLower))) {
          return true;
        }
      }
      return false;
    });
  };

  // Get content data based on active tab
  const getContentData = () => {
    let data = [];
    
    if (activeTab === "blog-posts") {
      data = blogPosts.map(blog => ({
        id: blog.id,
        no: blog.no,
        title: blog.title,
        author: blog.author,
        status: blog.status.name,
        statusColor: getStatusColor(blog.status.name),
        views: blog.views?.toLocaleString() || "0",
        publishDate: blog.publishDate || "Not published",
        category: blog.category,
        tags: blog.tags,
        slug: blog.slug
      }));
    } else if (activeTab === "faqs") {
      data = faqs.map(faq => ({
        id: faq.id,
        no: faq.no,
        title: faq.question,
        author: faq.author,
        status: faq.status.name,
        statusColor: getStatusColor(faq.status.name),
        views: faq.views?.toLocaleString() || "0",
        publishDate: faq.publishDate || "Not published",
        category: faq.category,
        subcategory: faq.subcategory,
        priority: faq.priority,
        tags: faq.tags
      }));
    } else {
      // Fallback for other content types
      switch (activeTab) {
        case "announcements":
          return [];
        case "roadmaps":
          return [];
        default:
          return [];
      }
    }
    
    // Apply client-side search filter
    return filterBySearch(data, searchTerm);
  };

  // Handle blog actions
  const handleBlogAction = async (action, blogId) => {
    try {
      switch (action) {
        case 'publish':
          await updateBlogStatus(blogId, 'published');
          break;
        case 'unpublish':
          await updateBlogStatus(blogId, 'draft');
          break;
        case 'archive':
          await updateBlogStatus(blogId, 'archived');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this blog post?')) {
            await deleteBlogPost(blogId);
          }
          break;
        case 'edit':
          setEditingBlogId(blogId);
          setEditModalOpen(true);
          break;
        default:
          break;
      }
      setShowActionsMenu(null);
    } catch (error) {
      console.error('Failed to perform action:', error);
    }
  };

  // Handle FAQ actions
  const handleFAQAction = async (action, faqId) => {
    try {
      switch (action) {
        case 'publish':
          await updateFAQStatus(faqId, 'published');
          break;
        case 'unpublish':
          await updateFAQStatus(faqId, 'draft');
          break;
        case 'archive':
          await updateFAQStatus(faqId, 'archived');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this FAQ?')) {
            await deleteFAQ(faqId);
          }
          break;
        default:
          break;
      }
      setShowActionsMenu(null);
    } catch (error) {
      console.error('Failed to perform FAQ action:', error);
    }
  };


  const contentData = getContentData();
  const isLoading = activeTab === "blog-posts" ? blogPostsLoading : 
                   activeTab === "faqs" ? faqsLoading : false;
  const currentPagination = activeTab === "blog-posts" ? blogPagination : 
                           activeTab === "faqs" ? faqPagination : null;

  const getTableHeaders = () => {
    switch (activeTab) {
      case "blog-posts":
        return ["Title", "Author", "Status", "Views", "Publish Date", "Actions"];
      case "announcements":
        return ["Announcement", "Author", "Status", "Views", "Publish Date", "Actions"];
      case "roadmaps":
        return ["Roadmap", "Author", "Status", "Views", "Publish Date", "Actions"];
      case "faqs":
        return ["Question", "Category", "Priority", "Status", "Views", "Actions"];
      default:
        return ["Title", "Author", "Status", "Views", "Publish Date", "Actions"];
    }
  };

  const getCreateButtonText = () => {
    switch (activeTab) {
      case "blog-posts":
        return "Create blog post";
      case "announcements":
        return "Create announcement";
      case "roadmaps":
        return "Create roadmap";
      case "faqs":
        return "Create FAQ";
      default:
        return "Create content";
    }
  };

  // Show loading state for blog posts
  if (activeTab === "blog-posts" && blogPostsLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {getTableHeaders().map((header, index) => (
                  <th
                    key={index}
                    className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[1, 2, 3].map((index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="h-6 bg-gray-200 rounded-full animate-pulse"></div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {getTableHeaders().map((header, index) => (
                <th
                  key={index}
                  className="text-left py-3 px-4 font-medium text-gray-600 text-xs sm:text-sm"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {contentData.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                <td className="py-4 px-4">
                  <div className="font-medium text-gray-900 text-sm sm:text-base">
                    {item.title}
                  </div>
                  {item.category && (
                    <div className="text-xs text-gray-500 mt-1">
                      {item.category}
                    </div>
                  )}
                </td>
                {activeTab === "faqs" ? (
                  <>
                    <td className="py-4 px-4 text-sm text-gray-600">{item.category}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {item.priority}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.statusColor}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">{item.views}</td>
                  </>
                ) : (
                  <>
                    <td className="py-4 px-4 text-sm text-gray-600">{item.author}</td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.statusColor}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-600">{item.views}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{item.publishDate}</td>
                  </>
                )}
                <td className="py-4 px-4">
                  <div
                    className="relative"
                    ref={showActionsMenu === item.id ? actionsMenuRef : null}
                  >
                    <button 
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setShowActionsMenu(showActionsMenu === item.id ? null : item.id)}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    {showActionsMenu === item.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                        <div className="py-1">
                          {activeTab === 'blog-posts' && (
                            <button
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={() => handleBlogAction('edit', item.id)}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </button>
                          )}
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => activeTab === 'faqs' ? handleFAQAction('publish', item.id) : handleBlogAction('publish', item.id)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Publish
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => activeTab === 'faqs' ? handleFAQAction('unpublish', item.id) : handleBlogAction('unpublish', item.id)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Unpublish
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => activeTab === 'faqs' ? handleFAQAction('archive', item.id) : handleBlogAction('archive', item.id)}
                          >
                            <Archive className="w-4 h-4 mr-2" />
                            Archive
                          </button>
                          <button
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            onClick={() => activeTab === 'faqs' ? handleFAQAction('delete', item.id) : handleBlogAction('delete', item.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State (when no content) */}
      {contentData.length === 0 && (
        <div className="text-center py-12">
          {["announcements", "roadmaps"].includes(activeTab) ? (
            <>
              <div className="text-gray-400 mb-4">
                <Star className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Coming soon</h3>
              <p className="text-gray-600">
                We&apos;re working on {activeTab.replace('-', ' ')}. Check back later.
              </p>
            </>
          ) : (
            <>
              <div className="text-gray-400 mb-4">
                <FileText className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
              <p className="text-gray-600 mb-4">
                Get started by creating your first {activeTab.replace('-', ' ')}.
              </p>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{getCreateButtonText()}</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Blog Modals */}
      {activeTab === "blog-posts" && (
        <>
        <CreateBlogModal 
          isOpen={showCreateModal} 
          onClose={() => setShowCreateModal(false)} 
        />
          <CreateBlogModal
            isOpen={editModalOpen}
            onClose={closeEditModal}
            mode="edit"
            blogId={editingBlogId}
          />
        </>
      )}

      {/* Create FAQ Modal */}
      {activeTab === "faqs" && (
        <CreateFAQModal 
          isOpen={showCreateModal} 
          onClose={() => setShowCreateModal(false)} 
        />
      )}
    </div>
  );
};

export default ContentTable;
